import { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Container,
  CssBaseline,
  DialogContentText,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  ThemeProvider,
  Typography,
  createTheme,
} from "@mui/material";
import { generateShuffledCards } from "../helper/helper-functions";
import { gridOptions } from "../helper/helper-objects";
import GameBoard from "./GameBoard";
import RestartButton from "./RestartButton";
import SuccessDialog from "./SuccessDialog";
import "./App.css";

const theme = createTheme({
  palette: {
    mode: "dark",
    primary: {
      main: "#ffe45e",
    },
    secondary: {
      main: "#7df9ff",
    },
    success: {
      main: "#72f1b8",
    },
    background: {
      default: "#0d1b2f",
      paper: "rgba(19, 45, 76, 0.8)",
    },
  },
  shape: {
    borderRadius: 10,
  },
  typography: {
    fontFamily: "'Trebuchet MS', 'Gill Sans', sans-serif",
    h2: {
      fontFamily: "'Arial Black', 'Trebuchet MS', sans-serif",
      letterSpacing: "0.04em",
      textTransform: "uppercase",
    },
    h4: {
      fontFamily: "'Arial Black', 'Trebuchet MS', sans-serif",
    },
    h5: {
      fontFamily: "'Arial Black', 'Trebuchet MS', sans-serif",
    },
    h6: {
      fontWeight: 700,
      letterSpacing: "0.04em",
    },
    button: {
      fontWeight: 800,
      letterSpacing: "0.05em",
      textTransform: "uppercase",
    },
  },
  components: {
    MuiPaper: {
      styleOverrides: {
        root: {
          backdropFilter: "blur(16px)",
          backgroundImage: "none",
        },
      },
    },
  },
});

const formatTime = (seconds) =>
  `${Math.floor(seconds / 60)
    .toString()
    .padStart(2, "0")}:${(seconds % 60).toString().padStart(2, "0")}`;

const statCardStyles = {
  flex: 1,
  minWidth: 140,
  px: 2.5,
  py: 2,
  borderRadius: 4,
  background:
    "linear-gradient(180deg, rgba(255,255,255,0.2) 0%, rgba(255,255,255,0.09) 100%)",
  border: "1px solid rgba(255,255,255,0.18)",
  boxShadow: "0 16px 28px rgba(3, 8, 20, 0.18)",
};

export default function App() {
  const [gridKey, setGridKey] = useState("4");
  const [cards, setCards] = useState([]);
  const [flippedIndices, setFlippedIndices] = useState([]);
  const [matchedCards, setMatchedCards] = useState([]);
  const [gameStarted, setGameStarted] = useState(false);
  const [gameFinished, setGameFinished] = useState(false);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [startTime, setStartTime] = useState(null);
  const [endTime, setEndTime] = useState(null);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [timerStarted, setTimerStarted] = useState(false);
  const [flips, setFlips] = useState(0);
  const resetFlipTimeoutRef = useRef(null);

  const { rows, cols } = gridOptions[gridKey];
  const numCards = rows * cols;

  useEffect(() => {
    if (matchedCards.length === numCards && numCards > 0) {
      setEndTime(Date.now());
      setGameFinished(true);
      setShowSuccessDialog(true);
    }
  }, [matchedCards, numCards]);

  useEffect(() => {
    let timer;
    if (timerStarted && gameStarted && !gameFinished) {
      timer = setInterval(() => {
        setElapsedTime((prev) => prev + 1);
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [timerStarted, gameStarted, gameFinished]);

  useEffect(() => {
    return () => {
      if (resetFlipTimeoutRef.current) {
        clearTimeout(resetFlipTimeoutRef.current);
      }
    };
  }, []);

  const clearPendingReset = () => {
    if (resetFlipTimeoutRef.current) {
      clearTimeout(resetFlipTimeoutRef.current);
      resetFlipTimeoutRef.current = null;
    }
  };

  const handleCardClick = (index) => {
    if (
      flippedIndices.length === 2 ||
      flippedIndices.includes(index) ||
      matchedCards.includes(index)
    ) {
      return;
    }

    if (!timerStarted) {
      setTimerStarted(true);
      setStartTime(Date.now());
    }

    const newFlipped = [...flippedIndices, index];
    setFlippedIndices(newFlipped);
    setFlips((prev) => prev + 1);

    if (newFlipped.length === 2) {
      const [firstIndex, secondIndex] = newFlipped;
      if (cards[firstIndex] === cards[secondIndex]) {
        setMatchedCards((prev) => [...prev, firstIndex, secondIndex]);
      }

      resetFlipTimeoutRef.current = setTimeout(() => {
        setFlippedIndices([]);
        resetFlipTimeoutRef.current = null;
      }, 1000);
    }
  };

  const handleStartGame = () => {
    clearPendingReset();
    setCards(generateShuffledCards(numCards / 2));
    setFlippedIndices([]);
    setMatchedCards([]);
    setGameFinished(false);
    setShowSuccessDialog(false);
    setGameStarted(true);
    setStartTime(null);
    setEndTime(null);
    setElapsedTime(0);
    setTimerStarted(false);
    setFlips(0);
  };

  const restartGame = () => {
    clearPendingReset();
    setCards([]);
    setFlippedIndices([]);
    setMatchedCards([]);
    setGameStarted(false);
    setGameFinished(false);
    setShowSuccessDialog(false);
    setStartTime(null);
    setEndTime(null);
    setElapsedTime(0);
    setTimerStarted(false);
    setFlips(0);
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box className="app-shell">
        <Container maxWidth="lg" className="game-panel">
          <Stack spacing={3} alignItems="center" textAlign="center">
            <Stack spacing={1.5} alignItems="center" sx={{ pt: { xs: 1, md: 3 } }}>
              <Chip
                label="Arcade Memory Challenge"
                color="secondary"
                sx={{
                  fontWeight: 800,
                  letterSpacing: "0.08em",
                  borderRadius: 2,
                  px: 1,
                  color: "#08314a",
                  backgroundColor: "secondary.main",
                }}
              />
              <Typography variant="h2" component="h1">
                Memory Game
              </Typography>
              <Typography
                variant="h6"
                sx={{ maxWidth: 720, color: "rgba(255,255,255,0.78)" }}
              >
                Flip fast, spot patterns, and clear the board before your focus slips.
              </Typography>
            </Stack>

            {!gameStarted && (
              <Paper
                elevation={0}
                sx={{
                  width: "min(100%, 760px)",
                  p: { xs: 3, md: 4 },
                  borderRadius: 3,
                  border: "1px solid rgba(255,255,255,0.18)",
                  boxShadow: "0 24px 52px rgba(0, 0, 0, 0.22)",
                  background:
                    "linear-gradient(180deg, rgba(29, 67, 109, 0.96), rgba(13, 31, 57, 0.92))",
                }}
              >
                <Stack spacing={3}>
                  <Stack
                    direction={{ xs: "column", md: "row" }}
                    spacing={2}
                    justifyContent="space-between"
                    alignItems={{ xs: "flex-start", md: "center" }}
                  >
                    <Box textAlign="left">
                      <Typography variant="h5" sx={{ mb: 1 }}>
                        Pick your arena
                      </Typography>
                      <DialogContentText sx={{ color: "rgba(255,255,255,0.72)" }}>
                        Smaller boards are quick warmups. Larger boards feel closer to a proper
                        arcade run.
                      </DialogContentText>
                    </Box>
                    <FormControl sx={{ minWidth: 160 }}>
                      <InputLabel id="grid-size-label">Grid Size:</InputLabel>
                      <Select
                        labelId="grid-size-label"
                        label="Grid Size:"
                        value={gridKey}
                        onChange={(e) => setGridKey(e.target.value)}
                      >
                        <MenuItem value="1">2 x 2</MenuItem>
                        <MenuItem value="2">2 x 3</MenuItem>
                        <MenuItem value="3">3 x 4</MenuItem>
                        <MenuItem value="4">4 x 4</MenuItem>
                      </Select>
                    </FormControl>
                  </Stack>

                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={2}
                    justifyContent="space-between"
                  >
                    <Paper elevation={0} sx={statCardStyles}>
                      <Typography variant="overline" sx={{ color: "secondary.main" }}>
                        Challenge
                      </Typography>
                      <Typography variant="h5">
                        {rows} x {cols}
                      </Typography>
                      <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.7)" }}>
                        {numCards / 2} image pairs to memorize
                      </Typography>
                    </Paper>
                    <Paper elevation={0} sx={statCardStyles}>
                      <Typography variant="overline" sx={{ color: "primary.main" }}>
                        Goal
                      </Typography>
                      <Typography variant="h5">Perfect Run</Typography>
                      <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.7)" }}>
                        Match every card with as few flips as possible
                      </Typography>
                    </Paper>
                  </Stack>

                  <Button
                    variant="contained"
                    color="primary"
                    size="large"
                    onClick={handleStartGame}
                    sx={{
                      alignSelf: { xs: "stretch", sm: "center" },
                      px: 5,
                      py: 1.5,
                      borderRadius: 2,
                      color: "#08314a",
                      boxShadow: "0 14px 28px rgba(255, 228, 94, 0.3)",
                    }}
                  >
                    Start Game
                  </Button>
                </Stack>
              </Paper>
            )}

            {gameStarted && (
              <Stack spacing={3} sx={{ width: "100%" }}>
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={2}
                  justifyContent="center"
                  sx={{ width: "min(100%, 760px)", mx: "auto" }}
                >
                  <Paper elevation={0} sx={statCardStyles}>
                    <Typography variant="overline" sx={{ color: "secondary.main" }}>
                      Time
                    </Typography>
                    <Typography variant="h4">Time: {elapsedTime} seconds</Typography>
                    <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.7)" }}>
                      Timer display: {formatTime(elapsedTime)}
                    </Typography>
                  </Paper>
                  <Paper elevation={0} sx={statCardStyles}>
                    <Typography variant="overline" sx={{ color: "primary.main" }}>
                      Flips
                    </Typography>
                    <Typography variant="h4">Flips: {flips}</Typography>
                    <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.7)" }}>
                      {matchedCards.length / 2} of {numCards / 2} pairs found
                    </Typography>
                  </Paper>
                </Stack>

                <GameBoard
                  cards={cards}
                  flippedIndices={flippedIndices}
                  matchedCards={matchedCards}
                  onCardClick={handleCardClick}
                  rows={rows}
                  cols={cols}
                />

                {!gameFinished && <RestartButton onRestart={restartGame} />}
                <SuccessDialog
                  open={showSuccessDialog}
                  onClose={restartGame}
                  duration={
                    endTime && startTime
                      ? Math.floor((endTime - startTime) / 1000)
                      : null
                  }
                  flips={flips}
                />
              </Stack>
            )}
          </Stack>
        </Container>
      </Box>
    </ThemeProvider>
  );
}
